import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Descriptions, Table, Tag, message, Button, Modal, Select, Space, Tooltip } from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import api from "../api";
import FilterToolbar from "../components/FilterToolbar";

const { Option } = Select;

const AttendanceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBatchModalVisible, setIsBatchModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tag, setTag] = useState(null);
  const [batchTag, setBatchTag] = useState(null);
  const [filterStatus, setFilterStatus] = useState(undefined);
  const [filterRange, setFilterRange] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);

  const handleExport = async () => {
    if (!sessionDetails || !sessionDetails.records) {
        message.warning("没有数据可导出");
        return;
    }

    try {
      message.loading({ content: '正在生成导出文件...', key: 'exporting' });
      const response = await api.post('/attendance/export', [parseInt(id)], {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `签到明细_${sessionDetails.session.class.name}_${new Date().toLocaleDateString()}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success({ content: '导出成功', key: 'exporting' });
    } catch (error) {
      console.error(error);
      message.error({ content: '导出失败', key: 'exporting' });
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/attendance/sessions/${id}`);
      const data = response.data;

      // Transform data
      const transformedSession = {
          ...data.session,
          class: data.class,
          teacher: { name: data.session.teacherName }
      };

      const signedRecords = data.stats.signedStudents.map(s => ({
          id: s.user.id,
          user: s.user,
          status: 'SIGNED_IN',
          signedAt: s.signedAt,
          method: s.method,
          deviceFingerprint: s.deviceFingerprint // Use actual data from backend
      }));

      const unsignedRecords = data.stats.unsignedStudents.map(u => ({
          id: u.user.id,
          user: u.user,
          status: u.tag ? 'TAGGED' : 'NOT_SIGNED_IN',
          tag: u.tag
      }));

      const allRecords = [...signedRecords, ...unsignedRecords];

      const transformedStats = {
          total: data.snapshotClassSize,
          signedIn: data.stats.signedCount,
          notSignedIn: data.stats.unsignedCount,
          attendanceRate: data.snapshotClassSize > 0 
              ? ((data.stats.signedCount / data.snapshotClassSize) * 100).toFixed(1) 
              : 0
      };

      setSessionDetails({ 
          session: transformedSession, 
          records: allRecords, 
          stats: transformedStats 
      });
      setFilteredRecords(allRecords);

    } catch (error) {
      message.error("获取签到详情失败");
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 静默刷新(不显示loading)
  const silentRefresh = useCallback(async () => {
    try {
      const response = await api.get(`/attendance/sessions/${id}`);
      const data = response.data;

      const transformedSession = {
          ...data.session,
          class: data.class,
          teacher: { name: data.session.teacherName }
      };

      const signedRecords = data.stats.signedStudents.map(s => ({
          id: s.user.id,
          user: s.user,
          status: 'SIGNED_IN',
          signedAt: s.signedAt,
          method: s.method,
          deviceFingerprint: s.deviceFingerprint
      }));

      const unsignedRecords = data.stats.unsignedStudents.map(u => ({
          id: u.user.id,
          user: u.user,
          status: u.tag ? 'TAGGED' : 'NOT_SIGNED_IN',
          tag: u.tag
      }));

      const allRecords = [...signedRecords, ...unsignedRecords];

      const transformedStats = {
          total: data.snapshotClassSize,
          signedIn: data.stats.signedCount,
          notSignedIn: data.stats.unsignedCount,
          attendanceRate: data.snapshotClassSize > 0 
              ? ((data.stats.signedCount / data.snapshotClassSize) * 100).toFixed(1) 
              : 0
      };

      setSessionDetails({ 
          session: transformedSession, 
          records: allRecords, 
          stats: transformedStats 
      });
      // 保持当前筛选条件
      setFilteredRecords(prev => {
        // 如果没有筛选条件,返回全部
        if (!filterStatus && (!filterRange || filterRange.length === 0)) {
          return allRecords;
        }
        return prev; // 保持当前筛选结果,让 useEffect 处理筛选
      });
    } catch (error) {
      console.error("静默刷新失败", error);
    }
  }, [id, filterStatus, filterRange]);

  useEffect(() => {
    fetchData();
    // 每5秒自动刷新一次
    refreshIntervalRef.current = setInterval(() => {
      silentRefresh();
    }, 5000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchData, silentRefresh]);

  useEffect(() => {
    if (!sessionDetails) return;
    let list = sessionDetails.records || [];
    if (filterStatus) {
      const s = filterStatus;
      list = list.filter(r => {
        if (s === 'SIGNED_IN') return r.status === 'SIGNED_IN';
        if (s === 'NOT_SIGNED_IN') return r.status === 'NOT_SIGNED_IN' && !r.tag;
        if (s === 'LEAVE' || s === 'ABSENT' || s === 'LATE' || s === 'OTHER') return r.tag === s;
        return true;
      });
    }
    if (filterRange && filterRange.length === 2) {
      const start = filterRange[0]?.startOf('day');
      const end = filterRange[1]?.endOf('day');
      list = list.filter(r => {
        if (!r.signedAt) return false;
        const t = new Date(r.signedAt).getTime();
        return t >= start.valueOf() && t <= end.valueOf();
      });
    }
    setFilteredRecords(list);
  }, [filterStatus, filterRange, sessionDetails]);

  const showModal = (record) => {
    setSelectedRecord(record);
    setTag(record.tag || 'ABSENT'); // Default to 'ABSENT' if no tag is set
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    if (!selectedRecord) return;
    try {
        await api.post(`/attendance/sessions/${id}/tag-unsigned`, { 
            studentId: String(selectedRecord.user.id), 
            tag 
        });
        message.success("标签更新成功");
        fetchData(); // Refresh data
    } catch (error) {
        message.error("标签更新失败");
    }
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
    setTag(null);
  };

  // 批量编辑标签
  const showBatchModal = () => {
    const unsignedSelected = selectedRowKeys.filter(key => {
      const record = filteredRecords.find(r => r.id === key);
      return record && record.status !== 'SIGNED_IN';
    });
    if (unsignedSelected.length === 0) {
      message.warning('请选择未签到的学员');
      return;
    }
    setBatchTag('ABSENT');
    setIsBatchModalVisible(true);
  };

  const handleBatchOk = async () => {
    const unsignedSelected = selectedRowKeys.filter(key => {
      const record = filteredRecords.find(r => r.id === key);
      return record && record.status !== 'SIGNED_IN';
    });
    
    try {
      await Promise.all(unsignedSelected.map(key => {
        const record = filteredRecords.find(r => r.id === key);
        return api.post(`/attendance/sessions/${id}/tag-unsigned`, {
          studentId: String(record.user.id),
          tag: batchTag
        });
      }));
      message.success(`成功更新 ${unsignedSelected.length} 名学员的标签`);
      setSelectedRowKeys([]);
      fetchData();
    } catch (error) {
      message.error('批量更新失败');
    }
    setIsBatchModalVisible(false);
  };

  const handleBatchCancel = () => {
    setIsBatchModalVisible(false);
    setBatchTag(null);
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.status === 'SIGNED_IN', // 已签到的不能选择
      'aria-label': `选择学员 ${record.user?.name || ''}`,
    }),
  };

  // 获取唯一值用于筛选
  const uniqueMajors = [...new Set(filteredRecords.map(r => r.user?.major).filter(Boolean))];
  const uniqueClassModes = [...new Set(filteredRecords.map(r => r.user?.classMode).filter(Boolean))];

  const columns = [
    { 
      title: "姓名", 
      dataIndex: ['user', 'name'], 
      key: "name",
      filterSearch: true,
      onFilter: (value, record) => record.user?.name?.includes(value),
      filters: [...new Set(filteredRecords.map(r => r.user?.name).filter(Boolean))].map(name => ({ text: name, value: name }))
    },
    { 
      title: "账号", 
      dataIndex: ['user', 'account'], 
      key: "account",
      filterSearch: true,
      onFilter: (value, record) => record.user?.account?.includes(value),
      filters: [...new Set(filteredRecords.map(r => r.user?.account).filter(Boolean))].map(acc => ({ text: acc, value: acc }))
    },
    { 
      title: "专业", 
      dataIndex: ['user', 'major'], 
      key: "major", 
      render: (text) => text || '-',
      filters: uniqueMajors.map(m => ({ text: m, value: m })),
      onFilter: (value, record) => record.user?.major === value
    },
    { 
      title: "上课方式", 
      dataIndex: ['user', 'classMode'], 
      key: "classMode", 
      render: (text) => text || '-',
      filters: uniqueClassModes.map(m => ({ text: m, value: m })),
      onFilter: (value, record) => record.user?.classMode === value
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: '扫码签到', value: 'SCAN' },
        { text: '手动补签', value: 'MANUAL' },
        { text: '未签到', value: 'NOT_SIGNED' },
        { text: '请假', value: 'LEAVE' },
        { text: '旷课', value: 'ABSENT' },
        { text: '迟到', value: 'LATE' },
      ],
      onFilter: (value, record) => {
        if (value === 'SCAN') return record.status === 'SIGNED_IN' && record.method !== 'manual';
        if (value === 'MANUAL') return record.status === 'SIGNED_IN' && record.method === 'manual';
        if (value === 'NOT_SIGNED') return record.status !== 'SIGNED_IN' && !record.tag;
        return record.tag === value;
      },
      render: (status, record) => {
        if (status === 'SIGNED_IN') {
            return <Tag color={record.method === "manual" ? "orange" : "green"}>{record.method === "manual" ? "手动补签" : "扫码"}</Tag>;
        } else {
            const tagMap = {
                'LEAVE': '请假',
                'ABSENT': '旷课',
                'LATE': '迟到',
                'OTHER': '其他'
            };
            const displayTag = tagMap[record.tag] || record.tag || "未签到";
            return <Tag color="red">{displayTag}</Tag>;
        }
      }
    },
    { 
      title: "签到时间", 
      dataIndex: "signedAt", 
      key: "signedAt",
      sorter: (a, b) => new Date(a.signedAt || 0) - new Date(b.signedAt || 0),
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    },
    {
        title: "设备指纹",
        dataIndex: "deviceFingerprint",
        key: "deviceFingerprint",
        ellipsis: true,
        render: (text) => <Tooltip title={text}>{text || '-'}</Tooltip>
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        record.status !== 'SIGNED_IN' && (
            <Button type="link" onClick={() => showModal(record)}>
              编辑标签
            </Button>
        )
      ),
    },
  ];

  if (loading) return <p>加载中...</p>;
  if (!sessionDetails) return <p>未找到签到会话</p>;

  const { session, stats } = sessionDetails;

  return (
    <div>
        <Space style={{marginBottom: 16}}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>返回</Button>
            <Button type="primary" onClick={handleExport}>导出Excel</Button>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>刷新</Button>
        </Space>
      <Descriptions title="签到详情" bordered size="small">
        <Descriptions.Item label="班级">{session.class.name}</Descriptions.Item>
        <Descriptions.Item label="发起人">{session.teacher.name}</Descriptions.Item>
        <Descriptions.Item label="时间窗口">
          {`${new Date(session.startedAt).toLocaleString()} - ${session.endedAt ? new Date(session.endedAt).toLocaleString() : '进行中'}`}
        </Descriptions.Item>
        <Descriptions.Item label="出勤率">
          <b>{`${stats.attendanceRate}%`}</b>
        </Descriptions.Item>
        <Descriptions.Item label="统计" span={2}>
          {`总人数: ${stats.total}, 已签: ${stats.signedIn}, 未签: ${stats.notSignedIn}`}
        </Descriptions.Item>
      </Descriptions>

      <FilterToolbar
        dateMode="range"
        dateValue={filterRange}
        onDateChange={setFilterRange}
        statusOptions={[
          { label: '已签到', value: 'SIGNED_IN' },
          { label: '未签到', value: 'NOT_SIGNED_IN' },
          { label: '请假', value: 'LEAVE' },
          { label: '旷课', value: 'ABSENT' },
          { label: '迟到', value: 'LATE' },
          { label: '其他', value: 'OTHER' },
        ]}
        statusValue={filterStatus}
        onStatusChange={setFilterStatus}
        onReset={() => { setFilterStatus(undefined); setFilterRange([]); setFilteredRecords(sessionDetails.records || []); }}
      />

      <div style={{ marginTop: 24, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 style={{ margin: 0 }}>学员明细</h4>
        <Button 
          type="primary" 
          onClick={showBatchModal}
          disabled={selectedRowKeys.length === 0}
        >
          批量编辑标签 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
        </Button>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredRecords}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1000 }}
      />

      <Modal title={`编辑标签 - ${selectedRecord?.user.name}`} open={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
        <Select value={tag} style={{ width: 200 }} onChange={setTag} aria-label="选择标签">
          <Option value="LEAVE">请假</Option>
          <Option value="ABSENT">旷课</Option>
          <Option value="LATE">迟到</Option>
          <Option value="OTHER">其他</Option>
        </Select>
      </Modal>

      <Modal 
        title={`批量编辑标签 (${selectedRowKeys.filter(key => {
          const record = filteredRecords.find(r => r.id === key);
          return record && record.status !== 'SIGNED_IN';
        }).length} 名未签到学员)`} 
        open={isBatchModalVisible} 
        onOk={handleBatchOk} 
        onCancel={handleBatchCancel}
      >
        <p>将选中的未签到学员标记为：</p>
        <Select value={batchTag} style={{ width: '100%' }} onChange={setBatchTag} aria-label="选择批量标签">
          <Option value="LEAVE">请假</Option>
          <Option value="ABSENT">旷课</Option>
          <Option value="LATE">迟到</Option>
          <Option value="OTHER">其他</Option>
        </Select>
      </Modal>
    </div>
  );
};

export default AttendanceDetailPage;
